'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'
import { SiteFooter } from '@/components/ui/site-footer'
import {
  Check,
  ChevronDown,
  X,
  Play,
  Target,
  Brain,
  PenTool,
  BarChart3,
  Mail,
  Rocket,
  Video,
  Settings,
  TrendingUp,
  Trophy,
  Map,
  Megaphone,
  ShieldCheck,
  Users,
  Zap,
  BookOpen,
  ArrowRight,
  Lock,
  Clock,
  Star,
} from 'lucide-react'

// ============================================================
// DATA
// ============================================================

const MODULOS_FUNDAMENTOS = [
  { icon: Map, title: 'O Mapa do Território', aulas: 5, free: true, desc: 'Visão panorâmica do universo de funis perpétuos.' },
  { icon: Brain, title: 'Psicologia da Decisão', aulas: 5, free: false, desc: 'Gatilhos mentais e mecanismos de decisão de compra.' },
  { icon: Video, title: 'Frameworks de Webinar', aulas: 7, free: false, desc: '7 modelos de webinar mais lucrativos do mundo.' },
  { icon: PenTool, title: 'Copywriting para Funis', aulas: 7, free: false, desc: 'Escrita persuasiva que vende no automático.' },
  { icon: Target, title: 'Arquitetura da Oferta', aulas: 6, free: false, desc: 'Oferta, precificação, garantia e stack slide.' },
  { icon: BarChart3, title: 'Planejamento Estratégico', aulas: 5, free: false, desc: 'Calendário, orçamento, métricas e blueprint.' },
]

const MODULOS_PRATICA = [
  { icon: Megaphone, title: 'Captura e Tráfego', aulas: 6, free: true, desc: 'Landing pages, lead magnets e tráfego pago.' },
  { icon: Play, title: 'Gravando seu Webinar', aulas: 6, free: false, desc: 'Do roteiro à edição final sem estúdio.' },
  { icon: Settings, title: 'Automação e Evergreen', aulas: 7, free: false, desc: 'Plataformas, deadline funnel e checkout.' },
  { icon: Mail, title: 'Sequências de Email', aulas: 6, free: false, desc: '5 sequências completas com templates prontos.' },
  { icon: TrendingUp, title: 'Otimização e Escala', aulas: 8, free: false, desc: 'Diagnóstico, testes A/B e escala vertical.' },
  { icon: Trophy, title: 'Projeto Final', aulas: 4, free: false, desc: 'Checklist, go live e plano de 90 dias.' },
]

const PESQUISADORES = [
  { nome: 'Robert Cialdini', area: 'Persuasão e influência' },
  { nome: 'Daniel Kahneman', area: 'Economia comportamental' },
  { nome: 'Russell Brunson', area: 'Funis de venda' },
  { nome: 'Jeff Walker', area: 'Product Launch Formula' },
  { nome: 'Amy Porterfield', area: 'Webinars de lista' },
  { nome: 'Ryan Deiss', area: 'Customer Value Journey' },
  { nome: 'Frank Kern', area: 'Behavioral Dynamic Response' },
  { nome: 'Dan Kennedy', area: 'Direct response marketing' },
  { nome: 'Eugene Schwartz', area: 'Níveis de consciência do comprador' },
  { nome: 'Gary Halbert', area: 'Copywriting clássico' },
  { nome: 'David Ogilvy', area: 'Publicidade científica' },
  { nome: 'Joe Sugarman', area: 'Gatilhos psicológicos de copy' },
  { nome: 'Dean Jackson', area: 'Before/during/after unit' },
  { nome: 'Todd Brown', area: 'Engenharia de marketing de funis' },
]

const PARA_QUEM = [
  { titulo: 'Experts e consultores', desc: 'Que querem vender conhecimento no automático sem depender de lançamentos.' },
  { titulo: 'Donos de cursos online', desc: 'Que estão cansados da montanha-russa de faturamento dos lançamentos.' },
  { titulo: 'Coaches e mentores', desc: 'Que precisam de um fluxo constante de leads qualificados para suas mentorias.' },
  { titulo: 'Infoprodutores', desc: 'Que querem sair do ciclo de lançamento-pânico-lançamento e ter previsibilidade.' },
  { titulo: 'Profissionais liberais', desc: 'Que têm expertise valiosa e querem transformar em produto digital escalável.' },
  { titulo: 'Agências de marketing', desc: 'Que querem oferecer funis perpétuos como serviço para seus clientes.' },
]

const FAQ_ITEMS = [
  {
    q: 'Preciso saber programar ou mexer com tecnologia?',
    a: 'Não. O curso foi pensado para experts de conteúdo, não programadores. Cada aula mostra o passo a passo com ferramentas visuais que não exigem código.',
  },
  {
    q: 'Qual a diferença entre funil perpétuo e lançamento?',
    a: 'No lançamento, você abre as vendas por alguns dias e depois fecha. No funil perpétuo, as vendas acontecem todos os dias no automático. Você constrói uma vez e ele funciona 24 horas por dia.',
  },
  {
    q: 'Funciona para qualquer nicho?',
    a: 'Sim. Os frameworks ensinados são universais. Funcionam para educação, saúde, finanças, desenvolvimento pessoal, negócios e qualquer área onde existe expertise para vender.',
  },
  {
    q: 'Quanto preciso investir em tráfego para começar?',
    a: 'O módulo Captura e Tráfego ensina estratégias orgânicas (custo zero) e tráfego pago a partir de R$ 20 por dia. Você decide o ritmo.',
  },
  {
    q: 'São 2 cursos separados?',
    a: 'São 2 cursos complementares vendidos juntos. Fundamentos traz a teoria, estratégia e copywriting. Prática traz a implementação passo a passo. Juntos, cobrem do zero ao funil no ar.',
  },
  {
    q: 'Tenho acesso vitalício?',
    a: 'Sim. Paga uma vez, acessa para sempre. Sem assinatura, sem renovação, sem taxa escondida.',
  },
  {
    q: 'E se eu não gostar?',
    a: 'Você tem 7 dias para experimentar. Se não fizer sentido para você, devolvemos 100% do valor. Sem perguntas, sem burocracia.',
  },
  {
    q: 'R$ 49,90 é o preço real? Parece barato demais.',
    a: 'É o preço de lançamento. São 72 aulas distribuídas em 12 módulos. Nosso objetivo é colocar esse conteúdo nas mãos do maior número possível de experts brasileiros.',
  },
]

// ============================================================
// FAQ Accordion
// ============================================================

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className="space-y-3">
      {FAQ_ITEMS.map((item, i) => (
        <div
          key={i}
          className="bg-white border border-stone-200/80 rounded-2xl overflow-hidden shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="w-full px-5 sm:px-6 py-4 flex items-center justify-between text-left cursor-pointer"
          >
            <span className="text-sm font-semibold text-stone-900 pr-4">{item.q}</span>
            <ChevronDown
              className={`w-4 h-4 text-stone-400 shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`}
            />
          </button>
          {openIndex === i && (
            <div className="px-5 sm:px-6 pb-4">
              <p className="text-sm text-stone-500 leading-relaxed">{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ============================================================
// MODULE CARD
// ============================================================

function ModuleCard({ mod, index }: { mod: typeof MODULOS_FUNDAMENTOS[number]; index: number }) {
  const Icon = mod.icon
  return (
    <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-[#006b5f]/10 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-[#006b5f]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-[#006b5f]">M{index + 1}</span>
            {mod.free && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                Gratuito
              </span>
            )}
          </div>
          <h4 className="text-sm font-bold text-stone-900 mb-1">{mod.title}</h4>
          <p className="text-xs text-stone-500 leading-relaxed">{mod.desc}</p>
          <p className="text-xs text-stone-400 mt-2">{mod.aulas} aulas</p>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// MAIN PAGE
// ============================================================

export default function FunilEvergreenPage() {
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  return (
    <div className="min-h-screen bg-[#fff8f5] text-stone-900 font-sans antialiased">

      {/* ====== NAV ====== */}
      <nav className="fixed w-full z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <Link href="/">
            <Image src="/logo.png" alt="eBuz" width={140} height={40} className="h-8 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#modulos" className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors">Módulos</a>
            <a href="#ciencia" className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors">Base Científica</a>
            <a href="#preco" className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors">Preço</a>
            <a href="#faq" className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-4">
            <Link href="/login" className="hidden sm:inline text-sm text-stone-600 hover:text-stone-900 transition-colors">
              Entrar
            </Link>
            <button
              onClick={() => setShowCheckout(true)}
              className="px-5 py-2 text-sm font-semibold bg-[#006b5f] hover:bg-[#005a50] text-white rounded-lg transition-colors cursor-pointer"
            >
              Quero Acesso
            </button>
          </div>
        </div>
      </nav>

      {/* ====== HERO ====== */}
      <header className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#006b5f]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-full px-4 py-1.5 mb-6">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-semibold text-stone-600">72 aulas - 12 módulos - 2 cursos completos</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
            Construa um funil que{' '}
            <span className="bg-gradient-to-r from-[#006b5f] to-emerald-500 bg-clip-text text-transparent">
              vende enquanto você dorme
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-stone-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            Da psicologia da decisão ao funil no ar. Domine os frameworks internacionais de venda perpétua
            e monte sua máquina de vendas automatizada do zero.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <button
              onClick={() => setShowCheckout(true)}
              className="w-full sm:w-auto px-8 py-4 bg-[#006b5f] hover:bg-[#005a50] text-white font-bold rounded-xl shadow-lg shadow-[#006b5f]/20 hover:shadow-xl transition-all text-lg cursor-pointer"
            >
              Quero os 2 Cursos por R$ 49,90
            </button>
            <a
              href="#modulos"
              className="w-full sm:w-auto px-8 py-4 bg-white hover:bg-stone-50 text-stone-700 font-semibold rounded-xl border border-stone-200 transition-colors text-center"
            >
              Ver grade completa
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-stone-500">
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Acesso vitalício
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Pagamento único
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Garantia de 7 dias
            </span>
          </div>
        </div>
      </header>

      <main>

        {/* ====== O QUE VOCÊ VAI APRENDER ====== */}
        <section className="py-20 sm:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Dois cursos. Uma missão.
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto">
                Fundamentos traz a ciência e a estratégia. Prática coloca tudo no ar.
                Juntos, formam o caminho completo para vender no automático.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Curso 1 */}
              <div className="bg-gradient-to-br from-[#006b5f]/5 to-emerald-50 rounded-3xl border border-[#006b5f]/10 p-8">
                <div className="inline-flex items-center gap-2 bg-[#006b5f]/10 rounded-full px-3 py-1 mb-4">
                  <BookOpen className="w-3.5 h-3.5 text-[#006b5f]" />
                  <span className="text-xs font-bold text-[#006b5f]">CURSO 1</span>
                </div>
                <h3 className="text-xl font-black text-stone-900 mb-2">Funil Evergreen - Fundamentos</h3>
                <p className="text-sm text-stone-500 mb-4">A ciência da venda perpétua</p>
                <div className="flex items-center gap-4 mb-6 text-sm text-stone-600">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> 35 aulas</span>
                  <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> 6 módulos</span>
                </div>
                <ul className="space-y-2">
                  {['Psicologia do comprador', 'Frameworks de webinar', 'Copywriting para funis', 'Arquitetura da oferta', 'Planejamento estratégico'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-stone-700">
                      <Check className="w-4 h-4 text-[#006b5f] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Curso 2 */}
              <div className="bg-gradient-to-br from-amber-500/5 to-orange-50 rounded-3xl border border-amber-500/10 p-8">
                <div className="inline-flex items-center gap-2 bg-amber-500/10 rounded-full px-3 py-1 mb-4">
                  <Rocket className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-bold text-amber-600">CURSO 2</span>
                </div>
                <h3 className="text-xl font-black text-stone-900 mb-2">Funil Evergreen - Prática</h3>
                <p className="text-sm text-stone-500 mb-4">Construindo sua máquina de vendas</p>
                <div className="flex items-center gap-4 mb-6 text-sm text-stone-600">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> 37 aulas</span>
                  <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> 6 módulos</span>
                </div>
                <ul className="space-y-2">
                  {['Landing pages e tráfego', 'Gravação de webinar', 'Automação evergreen', 'Sequências de email', 'Otimização e escala'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-stone-700">
                      <Check className="w-4 h-4 text-amber-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ====== GRADE DE MÓDULOS ====== */}
        <section id="modulos" className="py-20 sm:py-28">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                12 módulos. 72 aulas. Zero enrolação.
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto">
                Cada módulo resolve um problema específico. Cada aula entrega algo prático.
              </p>
            </div>

            {/* Fundamentos */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-[#006b5f] flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-black text-stone-900">Fundamentos</h3>
                <span className="text-xs text-stone-400">35 aulas</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {MODULOS_FUNDAMENTOS.map((mod, i) => (
                  <ModuleCard key={mod.title} mod={mod} index={i} />
                ))}
              </div>
            </div>

            {/* Prática */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                  <Rocket className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-black text-stone-900">Prática</h3>
                <span className="text-xs text-stone-400">37 aulas</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {MODULOS_PRATICA.map((mod, i) => (
                  <ModuleCard key={mod.title} mod={mod} index={i + 7} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ====== BASE CIENTÍFICA ====== */}
        <section id="ciencia" className="py-20 sm:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Baseado em quem já vendeu bilhões
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto">
                O conteúdo desses 2 cursos é fundamentado em 14 pesquisadores e profissionais
                que moldaram o marketing digital moderno.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              {PESQUISADORES.map((p) => (
                <div key={p.nome} className="bg-stone-50 rounded-xl border border-stone-100 p-4 text-center">
                  <p className="text-sm font-bold text-stone-900 mb-1">{p.nome}</p>
                  <p className="text-[11px] text-stone-400 leading-tight">{p.area}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== PARA QUEM É ====== */}
        <section className="py-20 sm:py-28">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Para quem é esse combo?
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PARA_QUEM.map((item) => (
                <div key={item.titulo} className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
                  <div className="w-8 h-8 rounded-lg bg-[#006b5f]/10 flex items-center justify-center mb-4">
                    <Users className="w-4 h-4 text-[#006b5f]" />
                  </div>
                  <h4 className="text-sm font-bold text-stone-900 mb-2">{item.titulo}</h4>
                  <p className="text-sm text-stone-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== PRICING ====== */}
        <section id="preco" className="py-20 sm:py-28 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Investimento único. Acesso vitalício.
              </h2>
              <p className="text-stone-500">
                Sem assinatura. Sem renovação. Sem surpresas.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#006b5f] to-emerald-600 rounded-3xl p-8 sm:p-10 text-white shadow-xl shadow-[#006b5f]/20">
              <div className="text-center mb-8">
                <p className="text-emerald-200 text-sm font-semibold mb-2">COMBO COMPLETO</p>
                <h3 className="text-2xl font-black mb-1">Funil Evergreen - Fundamentos + Prática</h3>
                <p className="text-emerald-100 text-sm">72 aulas em 12 módulos</p>
              </div>

              <div className="flex items-end justify-center gap-2 mb-6">
                <span className="text-sm text-emerald-200 line-through">R$ 297</span>
                <span className="text-5xl font-black">R$ 49,90</span>
              </div>

              <ul className="grid sm:grid-cols-2 gap-3 mb-8 max-w-md mx-auto">
                {[
                  '35 aulas de Fundamentos',
                  '37 aulas de Prática',
                  'Acesso vitalício',
                  'Atualizações gratuitas',
                  'Garantia de 7 dias',
                  '2 módulos gratuitos para testar',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-emerald-50">
                    <Check className="w-4 h-4 text-emerald-300 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="text-center">
                <button
                  onClick={() => setShowCheckout(true)}
                  className="w-full sm:w-auto px-10 py-4 bg-white text-[#006b5f] font-bold rounded-xl hover:bg-emerald-50 transition-colors text-lg shadow-lg cursor-pointer"
                >
                  Quero Acesso Agora
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </button>
                <p className="text-xs text-emerald-200 mt-3 flex items-center justify-center gap-1">
                  <Lock className="w-3 h-3" /> Pagamento seguro via Pix, cartão ou boleto
                </p>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-stone-400">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> Garantia incondicional de 7 dias
              </span>
            </div>
          </div>
        </section>

        {/* ====== FAQ ====== */}
        <section id="faq" className="py-20 sm:py-28">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Perguntas frequentes
              </h2>
            </div>
            <FaqAccordion />
          </div>
        </section>

        {/* ====== FOOTER CTA ====== */}
        <section className="py-20 sm:py-28 bg-stone-900 text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
              Cada dia sem funil é dinheiro na mesa.
            </h2>
            <p className="text-stone-400 max-w-xl mx-auto mb-8">
              Enquanto você lê isso, funis perpétuos estão vendendo no automático para seus concorrentes.
              A diferença entre quem fatura e quem assiste é uma decisão.
            </p>
            <button
              onClick={() => setShowCheckout(true)}
              className="px-10 py-4 bg-[#006b5f] hover:bg-[#005a50] text-white font-bold rounded-xl shadow-lg shadow-[#006b5f]/30 hover:shadow-xl transition-all text-lg cursor-pointer"
            >
              Quero Construir Meu Funil por R$ 49,90
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </button>
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* ====== CHECKOUT MODAL ====== */}
      {showCheckout && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCheckout(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white rounded-t-2xl border-b border-stone-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-bold text-stone-900">Finalizar compra</h3>
                <p className="text-sm text-stone-500">Funil Evergreen - Combo Completo - R$ 49,90</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="text-stone-400 hover:text-stone-600 p-1 cursor-pointer">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {checkoutError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {checkoutError}
                </div>
              )}
              <TransparentCheckout
                channelSlug="funil-evergreen"
                buyerName={buyerName}
                buyerEmail={buyerEmail}
                buyerPhone={buyerPhone}
                buyerCpf={buyerCpf}
                onSuccess={(paymentId) => {
                  router.push('/obrigado?payment=' + paymentId)
                }}
                onError={(error) => {
                  setCheckoutError(error)
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
